import { bibleserverUrlFor, bibleserverLabel, parseReference } from './bible_links.js';
import { renderCheckedHtml, summaryElementHtml } from './answer_checks.js';
import { getBlocks, addBlock, clearBlocks, onBlocksChanged } from './context_state.js';
import { isBridgeDetected, openInLogos } from './context_panel.js';
import { isMobile } from './platform.js';
import { openReportDialog, setContextProvider, stageRequestFailure } from './error_reporter.js';
import { streamAnswer } from './answer_stream.js';
import { applyPrefill, pickStartingPrompt, takePrefillPersona } from './chat_prefill.js';
import { EXPIRED_THREAD_TEXT, guestRemainingParts } from './guest_remaining.js';
import { setShareTarget } from './share.js';

export function initializeChatPage() {
    const frageForm = document.getElementById('frageForm');
    if (!frageForm) return;

    // --- DOM Elements ---
    const frageInput = document.getElementById('frage');
    const sendButton = document.querySelector('#frageForm button[type="submit"]');
    const sendButtonIcon = document.getElementById('sendButtonIcon');
    const inputHint = document.getElementById('inputHint');
    const chatMessages = document.getElementById('chatMessages');
    const loadingIndicator = document.getElementById('loading-indicator');
    const llmSettingsBtn = document.getElementById('llmSettingsBtn');
    const fileUploadBtn = document.getElementById('fileUploadBtn');
    const fileInput = document.getElementById('fileInput');
    const fileBadge = document.getElementById('fileBadge');
    const fileNameLabel = document.getElementById('fileNameLabel');
    const removeFileBtn = document.getElementById('removeFileBtn');
    const personaBtn = document.getElementById('personaBtn');
    const personaNameDisplay = document.getElementById('personaNameDisplay');
    const downloadButton = document.getElementById('download_md');
    // Guest-only controls (ADR-0083 decision 5); absent for accounts.
    const guestNewChatBtn = document.getElementById('guestNewChatBtn');
    const guestRemaining = document.getElementById('guestRemaining');
    const chatEmptyState = document.getElementById('chatEmptyState');

    // --- State Management ---
    let currentConversationId = null;
    let selectedLlmId = null;
    let selectedPromptId = null;
    let availablePrompts = [];
    let availableLlms = [];
    let lastQuestion = null;
    let lastAnswer = null;
    let inFlightRequest = null;
    // Read before the prompt list arrives (ADR-0077 decision 7).
    const prefillPersona = takePrefillPersona(frageInput);

    function showChatMessages() {
        if (chatEmptyState) chatEmptyState.classList.add('d-none');
        chatMessages.classList.remove('d-none');
    }

    function showEmptyState() {
        chatMessages.classList.add('d-none');
        if (chatEmptyState) chatEmptyState.classList.remove('d-none');
    }

    // --- Helpers ---

    // There is no total-duration timeout any more (ADR-0050 FR-15). Under
    // streaming "long" is no longer a failure signal — "silent" is — so the
    // idle timer in answer_stream.js watches for 90 seconds without a byte.

    /**
     * Maps a failed axios request to a user-facing German message.
     */
    function describeRequestError(error) {
        if (error.code === 'ECONNABORTED') {
            return 'Die Anfrage hat zu lange gedauert (Zeitüberschreitung). Bitte versuchen Sie es erneut.';
        }
        // 503 and 422 are the two statuses whose body is German and written for
        // the reader: the outage (ADR-0055 FR-31) and the question too large to
        // answer in one pass (ADR-0081). Every other ChatError message here is
        // an English service string that must not surface, which is why only
        // these two forward the body instead of a generic sentence.
        if (error.response?.status === 503) {
            return error.response.data?.error || 'Der Dienst ist derzeit gestört. Wir wissen davon.';
        }
        if (error.response?.status === 422) {
            return error.response.data?.error || 'Die Frage war zu umfangreich. Bitte teile sie auf.';
        }
        if (error.response?.status === 429) {
            return 'Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment und versuchen Sie es erneut.';
        }
        return 'Ein Fehler ist aufgetreten.';
    }

    /**
     * Safely extracts the ID string from a MongoDB object or a simple ID field.
     */
    function getItemId(item) {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (item._id) {
            if (typeof item._id === 'string') return item._id;
            if (item._id.$oid) return item._id.$oid;
        }
        if (item.id) return item.id;
        return null;
    }

    // --- Popover UI Helpers ---

    // Built through DOM APIs and textContent, never as an HTML string: `name`
    // and `description` are free text the user typed into their own persona
    // (POST /api/prompts stores both unescaped), so interpolating them here put
    // `<img src=x onerror=...>` straight into innerHTML. Same reasoning as
    // buildSourcesFooter() below. Returning an element also means the popover no
    // longer needs `sanitize: false` to render it.
    function renderPopoverList(data, currentId, type) {
        const menu = document.createElement('div');
        menu.className = 'popover-menu';
        data.forEach(item => {
            const itemId = getItemId(item);
            const row = document.createElement('div');
            row.className = 'popover-list-item';
            if (String(itemId) === String(currentId)) row.classList.add('active');
            row.dataset.id = itemId;
            row.dataset.type = type;

            const name = document.createElement('span');
            name.className = 'item-name';
            name.textContent = item.name || '';
            row.appendChild(name);

            const desc = document.createElement('span');
            desc.className = 'item-desc';
            desc.textContent = item.description || '';
            row.appendChild(desc);

            menu.appendChild(row);
        });
        return menu;
    }

    function initSelectionPopover(triggerEl, data, currentIdGetter, type, onSelect) {
        if (!triggerEl) return;

        const existingPopover = bootstrap.Popover.getInstance(triggerEl);
        if (existingPopover) existingPopover.dispose();

        new bootstrap.Popover(triggerEl, {
            html: true,
            trigger: 'click',
            placement: 'top',
            content: () => renderPopoverList(data, currentIdGetter(), type)
        });

        triggerEl.addEventListener('shown.bs.popover', () => {
            const popoverBody = document.querySelector('.popover-body');
            if (popoverBody) {
                popoverBody.querySelectorAll('.popover-list-item').forEach(item => {
                    item.addEventListener('click', () => {
                        const newId = item.getAttribute('data-id');
                        onSelect(newId);
                        const popoverInstance = bootstrap.Popover.getInstance(triggerEl);
                        if (popoverInstance) popoverInstance.hide();
                    });
                });
            }
        });
    }

    function updatePersonaUI() {
        if (!personaBtn || !personaNameDisplay) return;

        const activePrompt = availablePrompts.find(p => String(getItemId(p)) === String(selectedPromptId));
        
        if (activePrompt) {
            personaBtn.setAttribute('title', `Aktive Persona: ${activePrompt.name}`);
            
            // Always show the name next to the icon (like "Tools" in the screenshot)
            personaNameDisplay.textContent = activePrompt.name;
            personaNameDisplay.classList.remove('d-none');
            
            if (!activePrompt.is_standard) {
                personaBtn.classList.add('text-accent');
            } else {
                personaBtn.classList.remove('text-accent');
            }
        }
    }

    function updateLlmUI() {
        if (!llmSettingsBtn) return;
        const activeLlm = availableLlms.find(l => String(l.id) === String(selectedLlmId));
        if (activeLlm) {
            llmSettingsBtn.setAttribute('title', `LLM-Einstellungen: ${activeLlm.name}`);
        }
    }

    // --- Core Functions ---

    function clearChatDisplay() {
        chatMessages.innerHTML = '';
        showEmptyState();
        lastQuestion = null;
        lastAnswer = null;
        downloadButton.style.display = 'none';
    }

    function resetConversation() {
        clearChatDisplay();
        currentConversationId = null;
        setShareTarget(null);
    }

    function createUserMessage(content) {
        // Sanitize the echoed user input: the live submit path passes the raw
        // text typed by the user straight into innerHTML, so an input like
        // `<img src=x onerror=...>` would otherwise execute (stored XSS, since
        // the message is also persisted and re-rendered). DOMPurify is
        // idempotent, so pre-sanitized history content stays unaffected.
        const safeContent = DOMPurify.sanitize(content);
        return `<div class="chat-message user-message"><div class="message-content">${safeContent}</div></div>`;
    }

    const SOURCE_ICONS = {
        upload: 'fas fa-paperclip',
        bible_passage: 'fas fa-book-open',
        logos_note: 'fas fa-note-sticky',
        logos_excerpt: 'fas fa-quote-right'
    };

    // Titles and locators are user-controlled: build the footer via DOM APIs
    // and textContent so they are always escaped.
    function buildSourcesFooter(sources) {
        if (!sources || sources.length === 0) return '';
        const container = document.createElement('div');
        container.className = 'answer-sources mt-2 pt-2 border-top text-muted small';
        const heading = document.createElement('div');
        heading.className = 'fw-semibold';
        heading.textContent = 'Verwendete Quellen:';
        container.appendChild(heading);
        sources.forEach(source => {
            const line = document.createElement('div');
            const icon = document.createElement('i');
            icon.className = `${SOURCE_ICONS[source.type] || 'fas fa-file'} me-1`;
            line.appendChild(icon);
            const url = source.type === 'bible_passage' ? bibleserverUrlFor(source.locator) : null;
            if (url) {
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.target = '_blank';
                anchor.rel = 'noopener';
                anchor.title = bibleserverLabel(Boolean(parseReference(source.locator)?.english));
                anchor.textContent = source.locator || source.title;
                line.appendChild(anchor);
            } else {
                const label = document.createElement('span');
                label.textContent = source.title || source.locator || 'Quelle';
                line.appendChild(label);
            }
            // Sources whose locator is a Logos URL get an "open" button —
            // only when the bridge health probe has succeeded (panel opened).
            if (isBridgeDetected() && /^logos(ref|res):/i.test(source.locator || '')) {
                const openBtn = document.createElement('button');
                openBtn.type = 'button';
                openBtn.className = 'btn btn-link btn-sm p-0 ms-2 open-in-logos';
                openBtn.dataset.url = source.locator;
                openBtn.textContent = 'In Logos öffnen';
                line.appendChild(openBtn);
            }
            container.appendChild(line);
        });
        return container.outerHTML;
    }

    // The footer is inserted as an HTML string, so listeners attached while
    // building it are lost — handle "In Logos öffnen" clicks via delegation.
    chatMessages.addEventListener('click', event => {
        const button = event.target.closest('.open-in-logos');
        if (!button) return;
        openInLogos(button.dataset.url).catch(() => {
            button.textContent = 'Öffnen fehlgeschlagen — läuft die Bridge noch?';
            button.classList.add('text-danger');
        });
    });

    // Same delegation reason as the footer above, and the same container: the
    // chat appends with `chatMessages.innerHTML += ...`, which re-parses the
    // whole container and destroys every listener bound to a child. A handler
    // attached to the button at render time would work for the first error
    // bubble and be dead for every one after it (ADR-0032).
    chatMessages.addEventListener('click', event => {
        if (event.target.closest('.shepwise-report-error')) {
            openReportDialog('chat_request');
        }
    });

    // Lets the reporter name the active persona and model without knowing
    // anything else about this module.
    setContextProvider(() => ({
        persona_id: selectedPromptId || '',
        model_id: selectedLlmId || '',
    }));

    function buildReportControl() {
        return `
            <div class="mt-2">
                <button type="button" class="btn btn-link btn-sm p-0 shepwise-report-error">Fehler melden</button>
            </div>`;
    }

    function createBotMessage(content, isError = false, sources = []) {
        const errorClass = isError ? 'text-danger' : '';
        const reportControl = isError ? buildReportControl() : '';
        return `
            <div class="chat-message bot-message">
                <img src="/static/images/logo_shepwise.png" class="bot-avatar" alt="ShepWise AI">
                <div class="message-content ${errorClass}">${content}${buildSourcesFooter(sources)}${reportControl}</div>
            </div>`;
    }

    // `checks` is the answer's check result, present only while the display
    // switch is on (ADR-0085 FR-46); without it the answer links as before.
    function renderAnswerHtml(markdown, checks = null) {
        return renderCheckedHtml(DOMPurify.sanitize(marked.parse(markdown)), checks);
    }

    // --- The streaming answer bubble (ADR-0050 FR-17, FR-20) ---

    // Built through DOM APIs and appended, never `chatMessages.innerHTML += …`:
    // that re-parses the whole container, and doing it per chunk would re-parse
    // a growing conversation many times a second while destroying every
    // listener bound to a child (§3.6 of the debt report). The other four call
    // sites in this file are untouched — measure 18 stays open.
    //
    // The class names are copied from createBotMessage() deliberately: the e2e
    // suite selects `.chat-message.bot-message .message-content`, so the
    // streamed bubble has to be indistinguishable from the built one.
    function createStreamingBubble() {
        showChatMessages();
        const wrapper = document.createElement('div');
        wrapper.className = 'chat-message bot-message';
        const avatar = document.createElement('img');
        avatar.src = '/static/images/logo_shepwise.png';
        avatar.className = 'bot-avatar';
        avatar.alt = 'ShepWise AI';
        const content = document.createElement('div');
        content.className = 'message-content';
        wrapper.append(avatar, content);
        chatMessages.appendChild(wrapper);
        // The anchor for the sticky-bottom follow (FR-20), and it has to be
        // here. The submit handler scrolled to the bottom while only the
        // question was on screen; appending this bubble then adds its own
        // height — avatar plus padding, about 55 px — which is already more
        // than SCROLL_STICK_PX. Without this line isAtBottom() is false before
        // the first fragment arrives, so the follow never engages at all and
        // the reader has to chase the text by hand.
        followBottom();
        return { wrapper, content };
    }

    // Auto-scroll follows the text only while the user is already at the
    // bottom (FR-20). Measured *before* the content is mutated and applied
    // after — measuring afterwards compares against the new height and would
    // never report "at bottom" during growth. Re-measured every tick, so
    // scrolling up stops the following and returning to the bottom resumes it,
    // with no state to keep.
    const SCROLL_STICK_PX = 50;

    // Which element actually scrolls depends on the layout, and the three
    // layouts this page has genuinely differ — measured, not assumed:
    //
    //   anonymous desktop   the *page* scrolls. Nothing constrains the chat's
    //                       height there, so #chatMessages merely grows and
    //                       pushes the composer and footer down with it.
    //   Pro area            `.app-content` scrolls, because base_app.html puts
    //                       it inside `.app-body { height: 100vh; overflow:
    //                       hidden }`.
    //   narrow / landscape  #chatMessages scrolls itself, bounded by the
    //                       media queries' `height: 90vh` / `max-height: 50vh`.
    //
    // Hard-coding one of them is correct in that one and a silent no-op in the
    // other two: `el.scrollTop = …` on an element that does not scroll throws
    // nothing and does nothing. That is how the follow came to do nothing on
    // the desktop while every automated check still passed — the checks ran in
    // a viewport where #chatMessages happened to be the scroller.
    function scrollHost() {
        let el = chatMessages;
        while (el && el !== document.body) {
            const overflow = getComputedStyle(el).overflowY;
            const scrollable = overflow === 'auto' || overflow === 'scroll'
                || overflow === 'overlay';
            if (scrollable && el.scrollHeight > el.clientHeight + 1) return el;
            el = el.parentElement;
        }
        return document.scrollingElement || document.documentElement;
    }

    function isAtBottom(host) {
        const target = host || scrollHost();
        const slack = target.scrollHeight - target.scrollTop - target.clientHeight;
        return slack < SCROLL_STICK_PX;
    }

    // Resolved again rather than reused: growth can change *which* element
    // scrolls — the page does not scroll at all until the content outgrows the
    // viewport, and that moment usually arrives mid-answer.
    //
    // `behavior: 'instant'` is load-bearing, not a preference. Bootstrap ships
    // `@media (prefers-reduced-motion: no-preference) { :root { scroll-behavior:
    // smooth } }`, and `:root` is the scrolling element of the anonymous
    // desktop layout. A plain `scrollTop = …` there starts an *animation*, and
    // an animation is fatal rather than merely slow: 80 ms later the next tick
    // measures a scroll position that is still in flight, reads it as "the user
    // has scrolled away", and switches the follow off for the rest of the
    // answer. The property does not inherit, which is why #chatMessages — the
    // scroller in the narrow layouts — never showed this.
    function followBottom() {
        const host = scrollHost();
        host.scrollTo({ top: host.scrollHeight, behavior: 'instant' });
    }

    // Markdown is not parseable in fragments, so the accumulated text is
    // re-rendered whole on every tick; that is the price of headings and lists
    // appearing as they are written. Leading-plus-trailing: the first delta
    // renders immediately, then at most one render per 80 ms, with a
    // guaranteed trailing render so the last fragment is never left unrendered.
    const RENDER_THROTTLE_MS = 80;

    function createThrottledRenderer(content) {
        let timer = null;
        let pending = null;

        function paint(text) {
            const stick = isAtBottom();  // before the mutation, on the live host
            // Sanitised on every tick, not only at completion (NFR-5).
            // linkBibleReferences is deliberately absent (FR-18): on partial
            // text it sees "Johannes 3," without the "16", links the wrong
            // passage, and the wrong link is then in the DOM.
            content.innerHTML = DOMPurify.sanitize(marked.parse(text));
            if (stick) followBottom();
        }

        function render(text) {
            if (timer !== null) { pending = text; return; }
            paint(text);
            timer = setTimeout(() => {
                timer = null;
                if (pending === null) return;
                const trailing = pending;
                pending = null;
                render(trailing);
            }, RENDER_THROTTLE_MS);
        }

        // Called before the bubble is rebuilt on `done` or replaced on an
        // error, so a pending trailing paint cannot overwrite the final DOM.
        function stop() {
            if (timer !== null) clearTimeout(timer);
            timer = null;
            pending = null;
        }

        return { render, stop };
    }

    // The trim notice is diagnostic, not part of the answer (ADR-0045 FR-8):
    // it arrives in its own response field, is built with textContent like
    // buildSourcesFooter(), and is never persisted, never re-rendered when a
    // conversation is loaded from history, and never part of lastAnswer.
    function buildTrimNotice(trimmed) {
        if (!trimmed) return '';
        const line = document.createElement('div');
        line.className = 'history-trimmed-note text-muted small fst-italic mb-2';
        line.textContent = 'Diese Unterhaltung ist lang geworden — die frühesten '
            + 'Beiträge sind nicht mehr Teil des Kontexts. Wichtiges bitte kurz '
            + 'wiederholen.';
        return line.outerHTML;
    }

    // A gate notice is diagnostic in exactly the sense the trim note above is
    // (ADR-0053 FR-40): it arrives in its own response field, is built with
    // textContent, is never part of `lastAnswer`, never persisted, and never
    // re-rendered when a conversation is loaded from history.
    //
    // Three shapes, because three different things have happened to the
    // request — and none of them is an error. `text-danger` is deliberately not
    // among them: the message an exhausted allowance produces is a fact about a
    // ceiling, not a fault the user caused. A fourth shape, `gate-notice-hint`,
    // is not about the request at all: the complexity hint (ADR-0063) says that
    // a stronger model exists for a question like this one.
    const NOTICE_SHAPES = {
        daily_cap: 'gate-notice-stop',            // no answer was produced
        fair_use: 'gate-notice-degraded',         // an answer, from a smaller model
        premium_fallback: 'gate-notice-degraded',
        upload_quota: 'gate-notice-degraded',     // the attachment, not the answer
        credit_spent: 'gate-notice-charged',      // an answer, and it cost a unit
        complexity_hint: 'gate-notice-hint',      // an answer, and a stronger model exists
        truncated: 'gate-notice-degraded'         // an answer, cut at the length limit (ADR-0081)
    };

    // Every notice of one answer, in the order the server put them (ADR-0081):
    // the truncation first, then a gate notice or the hint. A server that sends
    // only `notice` still gets it rendered.
    function noticesOf(payload) {
        if (Array.isArray(payload.notices)) return payload.notices;
        return payload.notice ? [payload.notice] : [];
    }

    // The kinds whose text *is* the answer body. `daily_cap` is the one refusing
    // gate (FR-25), and `refusal_result` puts its message in `answer_markdown`
    // so a client that never learned about notices still shows it. This client
    // did learn, so it renders the block instead — with the CTA the plain text
    // cannot carry — and drops the body, rather than saying it twice.
    const REFUSAL_KINDS = new Set(['daily_cap']);

    // One line of text in a bot bubble. Built with textContent rather than
    // interpolated into a template string: the message can come from the
    // server, and the previous form put it straight into innerHTML.
    function buildLine(message, className) {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = message;
        return span.outerHTML;
    }

    // The red line the upload path shows for an actual failure.
    function buildErrorLine(message) {
        return buildLine(message, 'text-danger');
    }

    function isRefusal(notice) {
        return Boolean(notice && REFUSAL_KINDS.has(notice.kind));
    }

    // Notices that already carry the invitation the anonymous banner would repeat.
    const INVITING_KINDS = new Set(['daily_cap', 'complexity_hint']);

    function isInviting(notice) {
        return Boolean(notice && INVITING_KINDS.has(notice.kind));
    }

    function buildNotice(notice) {
        if (!notice || !notice.text) return '';
        const box = document.createElement('div');
        box.className = 'gate-notice ' + (NOTICE_SHAPES[notice.kind] || 'gate-notice-degraded');

        const line = document.createElement('div');
        line.textContent = notice.text;
        box.appendChild(line);

        const cta = notice.cta;
        // Only a same-origin path may become an href. The value is a constant
        // in `gate_notices.py` today, and this is what keeps it from becoming
        // an injection point if it ever stops being one.
        if (cta && cta.label && typeof cta.href === 'string' && cta.href.startsWith('/')) {
            const link = document.createElement('a');
            link.className = 'gate-notice-cta';
            link.href = cta.href;
            link.textContent = cta.label;
            box.appendChild(link);
        }
        return box.outerHTML;
    }

    // The whole bubble content for one completed answer, notice included.
    // One function, because the refusal case is a *choice between* the body and
    // the notice, and splitting it would leave that choice to be made twice.
    function buildAnswerContent(payload) {
        const notice = payload.notice;
        if (isRefusal(notice)) return buildNotice(notice);
        return buildTrimNotice(payload.history_trimmed)
            + renderAnswerHtml(payload.answer, payload.checks)
            + buildSourcesFooter(payload.sources)
            + summaryElementHtml(payload.checks)
            + noticesOf(payload).map(buildNotice).join('');
    }

    function updateSourceChip() {
        if (!fileBadge) return;
        const blocks = getBlocks();
        const count = blocks.length;
        if (count === 0) {
            fileBadge.classList.add('d-none');
            fileNameLabel.textContent = '';
        } else {
            fileNameLabel.textContent = count === 1
                ? blocks[0].title || '1 Quelle angehängt'
                : `${count} Quellen angehängt`;
            fileBadge.classList.remove('d-none');
        }
    }
    onBlocksChanged(updateSourceChip);

    function addBotMessage(htmlContent) {
        showChatMessages();
        chatMessages.innerHTML += createBotMessage(htmlContent);
        chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    function removeGuestRemaining() {
        chatMessages.querySelectorAll('.guest-remaining').forEach(line => line.remove());
    }

    // The inputs a guest has left today, under the latest answer (DEV_SPEC-0083
    // FR-23). The number is the server's; textContent keeps it inert.
    function renderGuestRemaining(remaining) {
        removeGuestRemaining();
        const parts = guestRemainingParts(remaining, guestRemaining.dataset.registeredCap);
        const line = document.createElement('div');
        line.className = 'guest-remaining text-muted small mt-2';
        const link = document.createElement('a');
        link.href = guestRemaining.dataset.registerUrl;
        link.textContent = parts.link;
        line.append(parts.before, link, parts.after);
        chatMessages.appendChild(line);
    }

    // A guest's answer is done: hold the thread, say what is left (ADR-0083).
    // A refusal carries no conversation_id, so the held one stays.
    function finishGuestAnswer(payload) {
        if (payload.conversation_id) currentConversationId = payload.conversation_id;
        // The refusal notice and the complexity hint already carry an
        // invitation as their CTA; a second one would repeat it.
        if (!noticesOf(payload).some(isInviting)) renderGuestRemaining(payload.guest_remaining);
        guestNewChatBtn.classList.remove('d-none');
    }

    // The held guest thread expired (FR-25): an expected end, not a fault, so
    // nothing is staged for an error report.
    function recoverExpiredThread(question, bubble) {
        currentConversationId = null;
        restoreAbortedQuestion(question, bubble);
        addBotMessage(buildLine(EXPIRED_THREAD_TEXT, 'text-muted'));
    }

    async function loadInitialData() {
        try {
            const [promptRes, llmRes] = await Promise.all([
                axios.get('/api/prompts'),
                llmSettingsBtn ? axios.get('/api/llms') : Promise.resolve({ data: [] })
            ]);

            // A stopped persona stays in its owner's list on the persona page,
            // marked stillgelegt (ADR-0062 FR-34) — but it is not offered as a
            // choice here, because choosing it can only produce the refusal
            // `chat_service.load_prompt` raises. Cosmetic either way: that
            // refusal is the enforcement (FR-32).
            availablePrompts = promptRes.data.filter(p => !p.moderation_blocked);
            // The starting persona (ADR-0082 decision 2): a field tile's, else
            // the account's default, else the site default, else the first
            // standard persona. A choice in the menu below never changes the
            // account's default.
            const standardPrompt = pickStartingPrompt(availablePrompts, {
                prefillName: prefillPersona,
                defaultId: window.DEFAULT_PERSONA_ID,
                defaultName: window.DEFAULT_PERSONA_NAME,
            });
            if (!selectedPromptId && standardPrompt) {
                selectedPromptId = getItemId(standardPrompt);
            }

            initSelectionPopover(personaBtn, availablePrompts, () => selectedPromptId, 'prompt', (id) => {
                selectedPromptId = id;
                updatePersonaUI();
            });
            updatePersonaUI();

            if (llmSettingsBtn) {
                availableLlms = llmRes.data;
                if (availableLlms.length > 0 && !selectedLlmId) {
                    selectedLlmId = availableLlms[0].id;
                }

                initSelectionPopover(llmSettingsBtn, availableLlms, () => selectedLlmId, 'llm', (id) => {
                    selectedLlmId = id;
                    updateLlmUI();
                });
                updateLlmUI();
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    }

    // --- The wait indicator (ADR-0081) ---
    //
    // It names what the server reports it is doing and counts the seconds since
    // the question was sent, so a long wait does not look like a hung page.
    // The phase comes from the `event: phase` frames; before the first one the
    // request is merely on its way.
    const WAIT_PHASES = {
        sent: 'Frage wird gesendet',
        analysing: 'Frage wird analysiert',
        thinking: 'Denkt nach'
    };
    const WAIT_HINT_AFTER_S = 20;
    const WAIT_HINT = 'Gründliche Antworten brauchen bis zu zwei Minuten.';

    function waitChild(container, tag, className) {
        const element = document.createElement(tag);
        element.className = className;
        container.appendChild(element);
        return element;
    }

    // The container is a polite live region. The seconds are hidden from screen
    // readers, which would otherwise announce every tick; the phase and the
    // hint are each announced once.
    function createWaitIndicator(container) {
        container.replaceChildren();
        const phase = waitChild(container, 'span', 'wait-phase');
        const seconds = waitChild(container, 'span', 'wait-seconds');
        seconds.setAttribute('aria-hidden', 'true');
        const hint = waitChild(container, 'div', 'wait-hint small d-none');
        hint.textContent = WAIT_HINT;
        let startedAt = 0;
        let ticker = null;

        // From the clock, not from counting ticks: a throttled background tab
        // shows the right number when it wakes.
        function tick() {
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            seconds.textContent = ` ${elapsed} s`;
            hint.classList.toggle('d-none', elapsed < WAIT_HINT_AFTER_S);
        }

        function setPhase(name) {
            if (!WAIT_PHASES[name]) return;
            phase.textContent = `${WAIT_PHASES[name]} …`;
        }

        return {
            start() {
                clearInterval(ticker);
                startedAt = Date.now();
                setPhase('sent');
                tick();
                container.classList.remove('d-none');
                ticker = setInterval(tick, 1000);
            },
            setPhase,
            // Idempotent: the submit handler calls it again after onText/onDone.
            stop() {
                clearInterval(ticker);
                ticker = null;
                container.classList.add('d-none');
            }
        };
    }

    const waitIndicator = createWaitIndicator(loadingIndicator);

    function startLoadingIndicator() {
        waitIndicator.start();
    }

    function stopLoadingIndicator() {
        waitIndicator.stop();
    }

    // --- Composer: sizing, send-button state, keyboard submit ---

    // The textarea ships with rows="1" and grows into its content; the ceiling
    // is `max-height` in style.css, which turns it into a scroller from there.
    function resizeInput() {
        frageInput.style.height = 'auto';
        frageInput.style.height = `${frageInput.scrollHeight}px`;
    }

    // Idle, the button is disabled while there is nothing to send. In flight it
    // stays enabled but aborts the request instead of starting another one.
    function updateSendButtonState() {
        if (inFlightRequest) return;
        sendButton.disabled = frageInput.value.trim() === '';
    }

    function resetInput() {
        frageInput.value = '';
        frageInput.style.height = '';
        updateSendButtonState();
    }

    function showSendingState(isSending) {
        sendButton.classList.toggle('is-sending', isSending);
        sendButtonIcon.className = isSending ? 'fas fa-stop' : 'fas fa-arrow-up';
        sendButton.setAttribute('aria-label', isSending ? 'Antwort abbrechen' : 'Frage absenden');
        if (isSending) sendButton.disabled = false;
        else updateSendButtonState();
    }

    frageInput.addEventListener('input', () => {
        resizeInput();
        updateSendButtonState();
    });

    // Enter sends and Shift+Enter breaks the line - but only where a hardware
    // keyboard is the norm. On a touch keyboard Enter has to remain a line
    // break, otherwise a paragraph becomes impossible to type.
    const sendsOnEnter = !isMobile();
    if (sendsOnEnter && inputHint) inputHint.classList.remove('d-none');

    frageInput.addEventListener('keydown', event => {
        if (!sendsOnEnter || event.key !== 'Enter' || event.shiftKey) return;
        // An IME confirms its candidate with Enter as well (umlaut composition,
        // dictation, CJK input); submitting here would ship a half-typed word.
        // keyCode 229 is the pre-`isComposing` spelling some engines still send.
        if (event.isComposing || event.keyCode === 229) return;
        event.preventDefault();
        frageForm.requestSubmit();
    });

    // Aborting is a client-side stop, and now a server-side one too: the
    // pipeline notices the disconnect, stores no conversation turn, and records
    // the tokens it already paid for with generation_status "aborted"
    // (ADR-0050 §3). So both bubbles go away and the question returns to the
    // input — after a reload the conversation shows exactly what the screen
    // showed, and the next turn's context carries no truncated sentence.
    function restoreAbortedQuestion(question, bubble) {
        if (bubble) bubble.wrapper.remove();
        const bubbles = chatMessages.querySelectorAll('.chat-message.user-message');
        const lastBubble = bubbles[bubbles.length - 1];
        if (lastBubble) lastBubble.remove();
        if (chatMessages.children.length === 0) showEmptyState();
        frageInput.value = question;
        resizeInput();
        updateSendButtonState();
        frageInput.focus();
    }

    // While a request is in flight the same button is the stop control - but the
    // abort cannot ride on the submit event: the textarea is `required` and was
    // just emptied, so the browser suppresses submission and pops its validation
    // bubble instead. Handling the click directly sidesteps the form.
    sendButton.addEventListener('click', event => {
        if (!inFlightRequest) return;
        event.preventDefault();
        inFlightRequest.abort();
    });

    frageForm.addEventListener('submit', async function (e) {
        e.preventDefault();

        // Enter during a running request does nothing; stopping is a deliberate
        // click on the stop button, not a side effect of the send shortcut.
        if (inFlightRequest) return;

        const question = frageInput.value.trim();
        if (!question) return;

        startLoadingIndicator();
        showChatMessages();

        removeGuestRemaining();
        chatMessages.innerHTML += createUserMessage(question);

        followBottom();
        resetInput();

        inFlightRequest = new AbortController();
        showSendingState(true);
        // A mouse click leaves the focus on the send button in Firefox and
        // WebKit, and that button is now the stop control: the next Enter would
        // throw the answer away. On a touch keyboard the focus stays where it
        // is, because focusing the input would open the keyboard.
        if (sendsOnEnter) frageInput.focus();

        await submitQuestion(question);

        inFlightRequest = null;
        showSendingState(false);
        stopLoadingIndicator();
        // Guarded, unlike before: a reader who scrolled up mid-answer must not
        // be yanked back the moment the answer completes (FR-20, US-2). For a
        // reader still at the bottom, onDone has already scrolled.
        if (isAtBottom()) followBottom();
    });

    function buildRequestBody(question) {
        const body = { message: question, prompt_id: selectedPromptId, llm_id: selectedLlmId };
        if (currentConversationId) body.conversation_id = currentConversationId;
        if (getBlocks().length > 0) body.context_blocks = getBlocks();
        return body;
    }

    function buildStreamHandlers(bubble, question) {
        const isAuthenticated = window.isUserAuthenticated;
        const url = isAuthenticated ? '/api/conversation/ask' : '/ask';
        const renderer = createThrottledRenderer(bubble.content);

        return {
            // The server names its phase before the first text (ADR-0081).
            onPhase(phase) {
                waitIndicator.setPhase(phase);
            },

            // The wait indicator covers the wait until the first token and is
            // stopped there, not at completion (FR-19). From this point the
            // answer itself is the progress indicator. Idempotent, so the
            // submit handler's own call in the unwind path stays harmless.
            onText(text) {
                stopLoadingIndicator();
                renderer.render(text);
            },

            // The completed bubble is rebuilt once through the existing
            // helpers, so the final DOM is what the non-streaming path
            // produced. `payload.answer` and not the accumulated text: the
            // accumulated text carries no footer, and the payload is
            // authoritative — a client may ignore every `text` event and still
            // be correct (FR-5).
            onDone(payload) {
                renderer.stop();
                stopLoadingIndicator();
                const stick = isAtBottom();
                const refused = isRefusal(payload.notice);
                bubble.content.innerHTML = buildAnswerContent(payload);
                if (isAuthenticated) {
                    currentConversationId = payload.conversation_id;
                    setShareTarget(currentConversationId);
                    if (currentConversationId) {
                        document.dispatchEvent(new CustomEvent('conversationcreated'));
                    }
                } else {
                    finishGuestAnswer(payload);
                }

                // A refusal leaves the previous answer downloadable and leaves
                // `lastAnswer` alone: there is nothing to download, and
                // overwriting it would replace a real answer the user still has
                // on screen with the sentence explaining why they got no second
                // one. The attached sources stay too — the question was never
                // asked, so the context is still the one they prepared.
                if (!refused) {
                    lastQuestion = question;
                    lastAnswer = payload.answer;
                    downloadButton.style.display = 'block';
                    clearBlocks();
                }
                // Last, after every DOM change this handler makes. The
                // remaining-inputs line and the download button appear here and add
                // height of their own; scrolling before them lands short of the
                // bottom by exactly their size — measured at 102 px anonymous
                // and 58 px in the Pro layout.
                if (stick) followBottom();
            },

            // A failure *after* text has arrived: the error bubble goes below
            // the partial text and the partial text stays (US-4). Since
            // ADR-0064 the `error` frame carries the status the failure would
            // have had as an HTTP response, and answer_stream.js hands it over
            // in the axios error shape: the staged report's http_status is that
            // status, and a 503 shows the German outage sentence. Every other
            // status shows the generic line; the server's English service
            // strings are never rendered.
            onError(error) {
                renderer.stop();
                stopLoadingIndicator();
                stageRequestFailure(error, url);
                chatMessages.innerHTML += createBotMessage(describeRequestError(error), true);
            }
        };
    }

    async function submitQuestion(question) {
        const isAuthenticated = window.isUserAuthenticated;
        // Declared outside the try because the catch reports it: a `const`
        // inside the block would be out of scope exactly where it is needed.
        const url = isAuthenticated ? '/api/conversation/ask' : '/ask';
        const bubble = createStreamingBubble();
        try {
            await streamAnswer(
                { url, body: buildRequestBody(question), signal: inFlightRequest.signal },
                buildStreamHandlers(bubble, question)
            );
        } catch (error) {
            if (error.code === 'ERR_CANCELED') {
                restoreAbortedQuestion(question, bubble);
                return;
            }
            if (error.response?.data?.code === 'conversation_not_found') {
                recoverExpiredThread(question, bubble);
                return;
            }
            // The empty bubble goes before the error bubble arrives, so a
            // failure before the first token leaves no stray empty bubble —
            // but only while it *is* empty. A transport failure (the server
            // dies mid-stream, the connection drops) reaches this catch rather
            // than onError, because a dying server sends no `error` frame.
            // Unconditionally removing the bubble here therefore deleted the
            // partial answer that the SSE error path deliberately leaves
            // standing (US-4) — text the user already paid for, and the one
            // difference the two error routes must not have. The first
            // fragment renders on the throttle's leading edge, so any text
            // that arrived is in the DOM by the time this runs.
            if (!bubble.content.textContent.trim()) bubble.wrapper.remove();
            // Before the bubble, not after: the control that will send the
            // status has to find it already staged.
            stageRequestFailure(error, url);
            chatMessages.innerHTML += createBotMessage(describeRequestError(error), true);
        }
    }

    if (downloadButton) {
        downloadButton.addEventListener('click', () => {
            if (lastQuestion && lastAnswer) {
                const markdownContent = `# Frage:\n${lastQuestion}\n\n# Antwort:\n${lastAnswer}`;
                const blob = new Blob([markdownContent], { type: 'text/markdown' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'frage_antwort.md';
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
            }
        });
    }

    if (fileUploadBtn) {
        fileUploadBtn.addEventListener('click', () => fileInput.click());
    }

    if (fileInput) {
        fileInput.addEventListener('change', async () => {
            const file = fileInput.files[0];
            if (!file) return;

            if (file.size > 2 * 1024 * 1024) {
                addBotMessage(buildErrorLine('Die Datei überschreitet die maximale Größe von 2 MB.'));
                fileInput.value = '';
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await axios.post('/api/upload-file', formData);
                addBlock({
                    type: 'upload',
                    title: response.data.filename,
                    locator: '',
                    content: response.data.text
                });
            } catch (error) {
                // An exhausted allowance is not a fault (ADR-0053 FR-17), so it
                // gets the notice shape and not the red one. Anything else here
                // really is a failure and keeps the shape it had.
                const notice = error.response?.data?.notice;
                if (notice) {
                    addBotMessage(buildNotice(notice));
                } else {
                    addBotMessage(buildErrorLine(
                        error.response?.data?.error || 'Fehler beim Hochladen der Datei.'
                    ));
                }
            } finally {
                fileInput.value = '';
            }
        });
    }

    if (removeFileBtn) {
        removeFileBtn.addEventListener('click', () => {
            clearBlocks();
        });
    }

    document.addEventListener('startnewchat', () => {
        resetConversation();
    });

    if (guestNewChatBtn) {
        guestNewChatBtn.addEventListener('click', () => {
            resetConversation();
            guestNewChatBtn.classList.add('d-none');
        });
    }

    loadInitialData();

    // A question from /chat?frage= (ADR-0077 decision 7): filled in, never sent.
    if (applyPrefill(frageInput)) {
        resizeInput();
        updateSendButtonState();
        frageInput.focus();
    }

    if (window.conversationData) {
        showChatMessages();
        try {
            let conversation = window.conversationData;
            if (typeof conversation === 'string') conversation = JSON.parse(conversation);
            currentConversationId = conversation._id;
            setShareTarget(currentConversationId);
            conversation.messages.forEach(message => {
                if (message.role === 'user') {
                    chatMessages.innerHTML += createUserMessage(DOMPurify.sanitize(marked.parse(message.content)));
                } else {
                    chatMessages.innerHTML += createBotMessage(
                        renderAnswerHtml(message.content, message.checks)
                        + summaryElementHtml(message.checks)
                    );
                }
            });
            chatMessages.scrollTop = chatMessages.scrollHeight;
        } catch (e) {}
    }
}